/*
 * Allocation capacity helpers.
 *
 * Turns the FS deliveries list (weekly + balance + hidden rows, as returned by
 * AdminAPI.getFSDeliveries) plus this week's per-company/product lead counts
 * (AdminAPI.getFSLeadsPeriod -> deliveryCounts) into a lookup the allocate
 * dropdowns can annotate themselves from, so an allocation session never has to
 * be cross-checked against the FS Deliveries screen.
 *
 * The cap maths deliberately mirrors FSDeliveriesTable.js (allocation +
 * extraAllocation, remaining = cap - leadCount) so the two screens can never
 * disagree for the same week.
 */

export function capacityKey(companyId, product) {
    return `${companyId}|${product}`
}

function buildCapacity(delivery, delivered) {
    const type = delivery.deliveryType || 'weekly'
    const active = delivery.active !== false

    // Balance buyers are capped in euro, not in leads.
    if (type === 'balance') {
        const balance = delivery.balance != null ? parseFloat(delivery.balance) : null
        return {
            type,
            active,
            cap: null,
            delivered,
            remaining: null,
            balance,
            isFull: balance != null && balance <= 0,
            label: balance != null ? `€${balance.toFixed(2)} balance` : `${delivered} allocated`
        }
    }

    // Hidden/hardcoded rows (auto-allocated, unlimited, or verified-app counted)
    // have no weekly cap — and for the Start Pension rows the lead count means
    // something different, so don't claim a count here.
    if (type === 'hidden' || delivery.allocation == null) {
        return {
            type,
            active,
            cap: null,
            delivered,
            remaining: null,
            balance: null,
            isFull: false,
            label: 'no weekly cap'
        }
    }

    const cap = (parseInt(delivery.allocation) || 0) + (parseInt(delivery.extraAllocation) || 0)
    const remaining = cap - delivered
    return {
        type,
        active,
        cap,
        delivered,
        remaining,
        balance: null,
        isFull: remaining <= 0,
        label: `${delivered} of ${cap}`
    }
}

/*
 * Returns { "companyId|product": capacity }.
 * A company should never hold both a weekly and a balance delivery for the same
 * product (allocateLead alerts if it does) — if it happens, the weekly row wins,
 * since that is the one that gets billed.
 */
export function buildCapacityMap(fsDeliveries = [], deliveryCounts = {}) {
    const map = {}
    for (const delivery of fsDeliveries) {
        if (!delivery || !delivery.companyId || !delivery.product) continue
        const key = capacityKey(delivery.companyId, delivery.product)
        const capacity = buildCapacity(delivery, deliveryCounts[key] || 0)
        const existing = map[key]
        if (existing && existing.type === 'weekly' && capacity.type !== 'weekly') continue
        map[key] = capacity
    }
    return map
}

export function getCapacity(capacityMap, companyId, product) {
    if (!capacityMap || !companyId || !product) return null
    return capacityMap[capacityKey(companyId, product)] || null
}

/*
 * Applies leads already allocated in this session (the bulk modal loops one lead
 * at a time) so the remaining count stays honest across a run.
 */
export function withPendingAllocations(capacity, pending) {
    if (!capacity || !pending) return capacity
    if (capacity.cap == null) return { ...capacity, delivered: capacity.delivered + pending }
    const delivered = capacity.delivered + pending
    const remaining = capacity.cap - delivered
    return {
        ...capacity,
        delivered,
        remaining,
        isFull: remaining <= 0,
        label: `${delivered} of ${capacity.cap}`
    }
}

/* "Actual Insurances (6 of 10)" */
export function companyOptionLabel(name, capacity) {
    if (!capacity) return name
    return `${name} (${capacity.label})`
}

/*
 * The warning shown before an allocation that would take a buyer past its cap.
 * Returns null when the allocation is within cap.
 */
export function overAllocationWarning(companyName, capacity, leadCount = 1) {
    if (!capacity) return null
    if (capacity.type === 'balance') {
        if (!capacity.isFull) return null
        return `${companyName} has no balance remaining (€${(capacity.balance || 0).toFixed(2)}).`
    }
    if (capacity.cap == null) return null
    const over = leadCount - capacity.remaining
    if (over <= 0) return null
    if (capacity.remaining <= 0) {
        return `${companyName} is already full for this week (${capacity.delivered} of ${capacity.cap}). ` +
            `Allocating ${leadCount === 1 ? 'this lead' : `these ${leadCount} leads`} will take them ${Math.abs(capacity.remaining) + leadCount} over cap.`
    }
    return `${companyName} only has ${capacity.remaining} of ${capacity.cap} left this week. ` +
        `Allocating ${leadCount} leads will take them ${over} over cap.`
}
